import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import {
  getCloudinaryCoverPhoto,
  getCloudinaryImageUrl,
  getCoverPhoto,
} from '../../util/listingImages';
import { isBookingProcessAlias } from '../../transactions/transaction';

import {
  AspectRatioWrapper,
  AvatarLarge,
  Button,
  NamedLink,
  ResponsiveImage,
} from '../../components';

import css from './ListingCard.module.css';
import IconCard from '../SavedCardDetails/IconCard/IconCard';
import BrandIconCard from '../BrandIconCard/BrandIconCard';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

function truncateText(text, maxLength) {
  if (text.length <= maxLength) {
    return text; // Return the original text if it's within or equal to the maxLength
  } else {
    return text.slice(0, maxLength) + '...'; // Truncate the text and add ellipsis
  }
}
const LazyImage = lazyLoadWithDimensions(ResponsiveImage, {
  loadAfterInitialRendering: 3000,
});

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === listingType
  );
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(
    price,
    config.currency,
    intl
  );
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage
            id="ListingCard.perUnit"
            values={{ unitType: publicData?.unitType }}
          />
        </div>
      ) : null}
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
    currentUser,
    onToggleFavorite,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const {
    title = '',
    description,
    price,
    publicData,
  } = currentListing.attributes;
  const {
    category,
    city,
    listingType,
    location,
    NumberofBedrooms,
    NumberofBathrooms,
    bedTypes,
    maximumGuests,
    transportationAvailable,
    cloudnaryImage = [],
  } = publicData || {};
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;

  const favorites =
    currentUser?.attributes?.profile?.publicData?.favorites || [];
  const normalizeId = value => (value ? String(value) : '');
  const [favoriteIds, setFavoriteIds] = React.useState(
    favorites.map(normalizeId)
  );

  React.useEffect(() => {
    const updatedFavorites =
      currentUser?.attributes?.profile?.publicData?.favorites || [];
    setFavoriteIds(updatedFavorites.map(normalizeId));
  }, [currentUser]);

  const projectId = normalizeId(id);
  const isFavorite = favoriteIds.includes(projectId);

  const handleToggleFavorite = (e, targetId) => {
    e.preventDefault();
    e.stopPropagation();

    const currentFavorites =
      currentUser?.attributes?.profile?.publicData?.favorites ||
      favoriteIds ||
      [];
    const normalizedTargetId = normalizeId(targetId);
    const alreadyFavorite = currentFavorites
      .map(normalizeId)
      .includes(normalizedTargetId);
    const updatedFavorites = alreadyFavorite
      ? currentFavorites.filter(
          favId => normalizeId(favId) !== normalizedTargetId
        )
      : [...currentFavorites.map(normalizeId), normalizedTargetId];

    setFavoriteIds(updatedFavorites);

    if (!onToggleFavorite) {
      console.warn('onToggleFavorite handler is missing');
      return;
    }

    const actionPayload = {
      publicData: { favorites: updatedFavorites },
    };

    onToggleFavorite(actionPayload).then(() => {
      setFavoriteIds(updatedFavorites);
    });
  };

  const getLocationLabel = address => {
    if (!address) return '';

    const parts = address
      .split(',')
      .map(p => p.trim())
      .filter(Boolean);

    if (parts.length < 2) {
      return address;
    }

    const country = parts[parts.length - 1];
    let stateOrRegion = parts.length >= 2 ? parts[parts.length - 2] : '';
    const city = parts.length >= 3 ? parts[parts.length - 3] : '';

    // Remove postal codes from state/region
    // Remove US ZIP codes (e.g., 12345 or 12345-6789)
    stateOrRegion = stateOrRegion
      .replace(/\b\d{5}(?:-\d{4})?\b/g, '')
      // Remove Canadian postal codes (e.g., A0G 3E0)
      .replace(/\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b/gi, '')
      // Remove UK postal codes (e.g., SW1A 1AA)
      .replace(/\b[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}\b/gi, '')
      // Remove other common postal code patterns
      .replace(/\b\d{4,6}\b/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      // Remove leading postal codes not caught above (e.g., "841 00 Ermoupolis" → "Ermoupolis")
      .replace(/^\d[\d\s]*\s+/, '')
      .trim();

    // Check if it's USA
    const usaCountries = [
      'United States',
      'United States of America',
      'USA',
      'US',
    ];

    const isUSA = usaCountries.some(
      c => c.toLowerCase() === country.toLowerCase()
    );

    if (isUSA && city && stateOrRegion) {
      // USA: show "City, State"
      return `${city}, ${stateOrRegion}`;
    }

    if (!isUSA && city && country) {
      // International: show "City, Country"
      // If the parsed "city" contains digits it's a street (e.g. "Psaron Street 22"),
      // so use stateOrRegion (which is the actual city) instead.
      if (/\d/.test(city)) {
        return stateOrRegion ? `${stateOrRegion}, ${country}` : country;
      }
      return `${city}, ${country}`;
    }

    // Fallbacks
    if (city && stateOrRegion) {
      return `${city}, ${stateOrRegion}`;
    }
    if (city) return city;
    if (stateOrRegion) return `${stateOrRegion}, ${country}`;
    return country || address;
  };

  const locationLabel = getLocationLabel(location?.address);
  const locationShow = locationLabel;
  const firstImage = getCoverPhoto(currentListing.images, publicData);
  const cloudinaryCoverPhotoUrl = getCloudinaryImageUrl(
    getCloudinaryCoverPhoto(publicData)
  );

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k =>
        k.startsWith(variantPrefix)
      )
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        {cloudinaryCoverPhotoUrl ? (
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            url={cloudinaryCoverPhotoUrl}
            // Responsive set: 250px (mobile) / 500px (desktop) / 1000px (2x). ResponsiveImage
            // emits these as a srcSet so the browser fetches only the size the card needs.
            variants={['search-card-mobile', 'search-card', 'search-card-2x']}
            sizes={renderSizes}
          />
        ) : (
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
        )}
      </AspectRatioWrapper>

      <div className={css.overImageContent}>
        <span className={css.countryLabel}>{locationShow}</span>
        <button
          type="button"
          className={css.heart}
          onClick={e => handleToggleFavorite(e, projectId)}
          onMouseDown={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
          aria-pressed={isFavorite}
        >
          <BrandIconCard type={isFavorite ? 'filledheart' : 'unfilledheart'} />
        </button>
      </div>

      <div className={css.info}>
        <div className={css.totalAmount}>
          ${Number(price?.amount || 0).toLocaleString('en-US')}{' '}
          <span>Monthly</span>
        </div>

        <div className={css.mainInfo}>
          <div className={css.searchTitle}>{truncateText(title, 60)}</div>
        </div>

        <div className={css.featureSelect}>
          <div className={css.item}>
            <IconCard brand="bed" />
            <span>{NumberofBedrooms}</span>
          </div>

          <div className={css.item}>
            <IconCard brand="toy" />
            <span>{NumberofBathrooms}</span>
          </div>

          <div className={css.item}>
            <IconCard brand="topbarGuestUsers" />
            <span>{maximumGuests}</span>
          </div>
        </div>
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
  currentUser: null,
  onToggleFavorite: null,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,
  currentUser: propTypes.currentUser,
  onToggleFavorite: func,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
